import React, { useState } from 'react';
import { Table, Input, Select, Space, Button } from 'antd';
import { MailOutlined, ShopOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WelcomeBanner from '../../Components/common/WelcomeBanner';
import { ApiErrorState, EmptyState } from '../../Components/common/StateViews';
import StatusTag from '../../Components/common/StatusTag';
import useApiData from '../../Hooks/useApiData';
import cioService from '../../Services/cioService';
import { ClientStatusLabels, ClientStatusColors, optionsFromLabels } from '../../Constants/enums';
import { formatDateTime } from '../../Utils/format';

const STATUS_OPTIONS = [{ value: '', label: 'All statuses' }, ...optionsFromLabels(ClientStatusLabels)];

const Customers = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status');

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(initialStatus !== null ? Number(initialStatus) : '');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data, loading, error, reload } = useApiData(
    () => cioService.getClients({ search, status, page, pageSize }),
    [search, status, page, pageSize]
  );

  const columns = [
    { title: 'Org ID', dataIndex: 'publicId' },
    { title: 'Account #', dataIndex: 'accountNumber' },
    { title: 'Customer', dataIndex: 'clientName' },
    { title: 'Users', key: 'users', render: (_, r) => r.subscription?.seatCount ?? '—' },
    { title: 'Admin Contact', dataIndex: 'adminContactEmail' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v) => <StatusTag value={v} labels={ClientStatusLabels} colors={ClientStatusColors} />,
    },
    { title: 'Created', dataIndex: 'createdOn', render: formatDateTime },
  ];

  return (
    <div>
      <WelcomeBanner
        title="Customers"
        subtitle="Enterprise customers and their onboarding lifecycle"
        icon={<ShopOutlined />}
        extra={
          <Button type="primary" icon={<MailOutlined />} onClick={() => navigate('/cio/invitations')}>
            Send Onboarding Invitation
          </Button>
        }
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          allowClear
          placeholder="Search by name"
          style={{ width: 260 }}
          onSearch={(value) => {
            setPage(1);
            setSearch(value);
          }}
        />
        <Select
          value={status}
          onChange={(value) => {
            setPage(1);
            setStatus(value);
          }}
          options={STATUS_OPTIONS}
          style={{ width: 220 }}
        />
      </Space>

      {error ? (
        <ApiErrorState error={error} onRetry={reload} />
      ) : (
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={data?.items || []}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <EmptyState description="No customers match this filter." /> }}
          onRow={(record) => ({
            onClick: () => navigate(`/cio/customers/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: data?.page || page,
            pageSize: data?.pageSize || pageSize,
            total: data?.totalCount || 0,
            showSizeChanger: true,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
        />
      )}
    </div>
  );
};

export default Customers;
